import type { ShipmentType } from '@b-stock/shipment-api-client'

import type { TaxComponentBreakdown } from '@/queries/canadianTaxComponentBreakdown.types'
import canadianTaxMaxExposureQuery from '@/queries/canadianTaxMaxExposureQuery'

import type { MaxTaxExposureData } from './MaxTaxExposureDisplay'

/**
 * Calls the listing service's `GET /v1/canadian-tax/listings/:id/max-exposure`
 * endpoint (T075, landed in svc/listing v16.6.0) and adapts the DTO
 * into the `MaxTaxExposureData` the `MaxTaxExposureDisplay` renders.
 *
 * Reading B is realised server-side: `committedBidAmount` is the max
 * of the form values passed here and the buyer's existing proxy max
 * for this auction (read via `BidsApi.getAccountBids`). The hook just
 * pipes the form values through the query key so cache invalidation
 * fires whenever the buyer edits the bid amount in the modal.
 *
 * Suppression contract:
 *   - No `buyerAddressId` → the bid-confirmation modal shouldn't have
 *     opened in this state (FR-008 gates bidding on a valid address),
 *     but defensively skip the query and return `undefined` so the
 *     container renders nothing.
 *   - Tanstack-Query in-flight / errored → also return `undefined`. The
 *     listing-service degrades to suppression for rate-lookup failure
 *     (it has no fallback rate), and bid-confirmation should mirror
 *     that — better to render no tax estimate than a stale or wrong one.
 */
const useMaxTaxExposure = ({
  isTaxable = true,
  listingId,
  formSingleBid,
  buyerAddressId,
  formProxyMaxBid,
  buyerFeeCents,
  shippingCents,
  shipmentType,
}: {
  isTaxable?: boolean
  listingId: string
  formSingleBid: number
  buyerAddressId: string | undefined
  formProxyMaxBid?: number
  // FP-2498 — client-computed inputs to the /calc BUYER_FEE + SHIPPING
  // taxable bases. Both are optional; when omitted the backend defaults
  // each to 0 (rendered as the $0.00 row per FP-2498 AC).
  buyerFeeCents?: number
  shippingCents?: number
  // FP-2496 — in the query key so a delivery-method change refetches.
  shipmentType?: ShipmentType
}): MaxTaxExposureData | undefined => {
  const { data, isError } = canadianTaxMaxExposureQuery.useWithOpts(
    [
      listingId,
      formSingleBid,
      buyerAddressId ?? '',
      formProxyMaxBid,
      buyerFeeCents,
      shippingCents,
      shipmentType,
    ],
    {
      enabled: isTaxable && Boolean(buyerAddressId) && formSingleBid > 0,
    }
  )

  // FP-3012 — svc/listing answers province 'NONE' when no Canadian tax
  // applies (non-CA address, or a supply crossing the border). Suppress
  // rather than render a $0.00 tax row on every US listing.
  if (
    !isTaxable ||
    !buyerAddressId ||
    isError ||
    !data ||
    data.province === 'NONE'
  ) {
    return undefined
  }

  // FP-2498 — svc/listing !907 adds an optional `components[]` field on
  // the DTO; the currently-published listing-api-client type omits it,
  // so read it through a narrow cast until the client republishes.
  const components = (data as { components?: TaxComponentBreakdown[] })
    .components

  return {
    committedBidAmount: data.committedBidAmount,
    province: data.province,
    applicableShippingRate: data.applicableShippingRate,
    buyerFee: data.buyerFee,
    aggregatedRate: data.aggregatedRate,
    maxTaxAmount: data.maxTaxAmount,
    ...(components && { components }),
  }
}

export default useMaxTaxExposure
